<template>
  <tab-layout :full-width="true">
    <div slot="tabContent" class="bg-white rounded mb-4 p-3">
      <page-heading>Competitors</page-heading>

      <div class="d-flex align-items-center mb-3">
        <app-button variant="outline-primary" size="sm" class="mr-2" :block="false" :loading="downloadingFormat === 'pdf'" :disabled="downloadingFormat != null" @click="downloadFile('pdf')">
          Download PDF
        </app-button>
        <app-button variant="outline-primary" size="sm" class="mr-2" :block="false" :loading="downloadingFormat === 'xlsx'" :disabled="downloadingFormat != null" @click="downloadFile('xlsx')">
          Download CSV
        </app-button>
        <app-button variant="outline-secondary" size="sm" :block="false" @click="resetFilters">
          Reset filters
        </app-button>
      </div>

      <div class="competitors-results" :class="{ 'competitors-results--loading': loading }">
        <div v-if="loading" class="table-overlay">
          <div class="table-spinner" />
        </div>

        <div class="competitors-table" style="min-height: 400px; font-size: 1.125rem">
        <table class="table table-sm table-borderless mb-0">
          <thead>
            <tr>
              <th>
                <div class="font-weight-bold mb-1">Competitor</div>
                <f-search-select
                  id="filter_competitor"
                  :options="filterOptions.members"
                  :value="filters.competitor_id"
                  :loading="filterLoading.members"
                  :loading-more="filterLoadingMore.members"
                  placeholder="Select competitor"
                  @open="loadFilterOptions('members')"
                  @search-change="searchOptions('members', $event)"
                  @load-more="loadMoreOptions('members')"
                  @input="setFilter('competitor_id', $event)"
                />
              </th>
              <th>
                <div class="font-weight-bold mb-1">Nomination Status</div>
                <f-search-select
                  id="filter_nomination_status"
                  :options="filterOptions.nomination_status"
                  :value="filters.nomination_status"
                  :loading="filterLoading.nomination_status"
                  placeholder="Select status"
                  @open="loadFilterOptions('nomination_status')"
                  @input="setFilter('nomination_status', $event)"
                />
              </th>
              <th>
                <div class="font-weight-bold mb-1">Horse</div>
                <f-search-select
                  id="filter_horse"
                  :options="filterOptions.horses"
                  :value="filters.horse_id"
                  :loading="filterLoading.horses"
                  :loading-more="filterLoadingMore.horses"
                  placeholder="Select horse"
                  @open="loadFilterOptions('horses')"
                  @search-change="searchOptions('horses', $event)"
                  @load-more="loadMoreOptions('horses')"
                  @input="setFilter('horse_id', $event)"
                />
              </th>
              <th>
                <div class="font-weight-bold mb-1">Year</div>
                <f-search-select
                  id="filter_year"
                  :options="filterOptions.years"
                  :value="filters.year"
                  :loading="filterLoading.years"
                  placeholder="Select year"
                  @open="loadFilterOptions('years')"
                  @input="setFilter('year', $event)"
                />
              </th>
              <th>
                <div class="font-weight-bold mb-1">Draft</div>
                <f-search-select
                  id="filter_draft"
                  :options="filterOptions.drafts"
                  :value="filters.draft_id"
                  :loading="filterLoading.drafts"
                  placeholder="Select draft"
                  @open="loadFilterOptions('drafts')"
                  @input="setFilter('draft_id', $event)"
                />
              </th>
              <th>
                <div class="font-weight-bold mb-1">Event</div>
                <f-search-select
                  id="filter_event"
                  :options="filterOptions.events"
                  :value="filters.event_id"
                  :loading="filterLoading.events"
                  placeholder="Select event"
                  @open="loadFilterOptions('events')"
                  @input="setFilter('event_id', $event)"
                />
              </th>
            </tr>
          </thead>
          <tbody v-if="competitors.length">
            <tr v-for="row in competitors" :key="row.id">
              <td class="font-weight-bold">
                {{ row.competitor_name }}
                <small v-if="row.competitor_deleted" class="text-primary ml-1">(deleted)</small>
                <span v-if="row.competitor_town"> from {{ row.competitor_town }}</span>
              </td>
              <td>{{ row.nomination_status_label }}</td>
              <td>
                {{ row.horse_name }}
                <small v-if="row.horse_deleted" class="text-primary ml-1">(deleted)</small>
              </td>
              <td>{{ row.draft_year }}</td>
              <td>{{ row.draft_name }}</td>
              <td>{{ row.event_name }}</td>
            </tr>
          </tbody>
          <tbody v-else-if="!loading">
            <tr>
              <td colspan="6">No competitors found.</td>
            </tr>
          </tbody>
        </table>
        </div>

        <pagination
          v-if="meta != null"
          :total-pages="meta.last_page"
          :page="meta.current_page"
          :on-change="setPage"
          :disabled="loading"
          class="mt-3"
        />
      </div>
    </div>
  </tab-layout>
</template>

<script>
import { defineComponent, onMounted } from '@vue/composition-api'
import TabLayout from '@/components/TabLayout'
import PageHeading from '@/components/PageHeading'
import AppButton from '@/components/AppButton'
import FSearchSelect from '@/components/FormFields/FSearchSelect'
import Pagination from '@/components/Pagination'
import useCompetitorsList from '@/composables/competitors/useCompetitorsList'

export default defineComponent({
  name: 'CompetitorsList',
  components: { TabLayout, PageHeading, AppButton, FSearchSelect, Pagination },
  setup() {
    const {
      competitors,
      meta,
      filters,
      filterOptions,
      filterLoading,
      filterLoadingMore,
      loading,
      downloadingFormat,
      fetchList,
      loadFilterOptions,
      loadMoreOptions,
      searchOptions,
      setFilter,
      resetFilters,
      setPage,
      downloadFile
    } = useCompetitorsList()

    onMounted(() => {
      fetchList()
    })

    return {
      competitors,
      meta,
      filters,
      filterOptions,
      filterLoading,
      filterLoadingMore,
      loading,
      downloadingFormat,
      loadFilterOptions,
      loadMoreOptions,
      searchOptions,
      setFilter,
      resetFilters,
      setPage,
      downloadFile
    }
  }
})
</script>

<style scoped>
.competitors-results {
  position: relative;
}

.competitors-results--loading > .competitors-table,
.competitors-results--loading > .pagination-container {
  opacity: 0.5;
  pointer-events: none;
}
</style>



// WEBPACK FOOTER //
// src/views/Competitors/CompetitorsList.vue